import React, { useCallback, useEffect, useRef } from 'react';
import moment from 'moment';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { getOpmData } from 'pages/Valuations/approaches/backsolveApproach/BacksolveApproach';
import {
  MATURITY_ALIAS,
  OPM_BACKSOLVE_DATE_ALIAS,
} from 'pages/Valuations/approaches/backsolveApproach/OPMInputTable/constants';
import { useGetRiskFreeRate } from 'services/hooks/allocation';
import { OPMInputTableProps } from './types';

export const getRiskFreeRateKey = (maturity: unknown, backsolveDate: unknown) => `${maturity}|${backsolveDate}`;

const OPMInputTable = ({
  spreadsheets,
  onChange,
  setPrevOpmData,
  isLedgerTable,
  componentId,
  onEmptyRiskFreeRateResolved,
}: OPMInputTableProps) => {
  const { opmInputSheet } = spreadsheets;
  const [getRiskFreeRateByDate] = useGetRiskFreeRate();
  const backsolveDate = opmInputSheet?.cells?.OPM_BACKSOLVE_DATE?.value;
  const maturityValue = opmInputSheet?.cells?.MATURITY?.value;
  const riskFreeRateCell = opmInputSheet?.cells?.RISK_FREE_RATE;
  const riskFreeRateValue = opmInputSheet?.cells?.RISK_FREE_RATE?.value;
  // The API never sends a rate, and the workbook evaluation turns the parser's undefined into a
  // numeric 0 (Cell.setValue), so an evaluated cell reads 0 until a fetch writes into it. Presence
  // is therefore truthiness: the cell cannot tell an unset rate apart from a fetched 0.00%.
  const hasRiskFreeRate = Boolean(riskFreeRateValue);

  // The workbook re-evaluates (and replaces every cell object) while the rate fetch is
  // in flight, e.g. right after the approach mounts. Writing the response into the cell
  // captured before that swap lands on an orphan and reverse-parses the new cell's 0 back
  // over it, so the write always goes through the latest cell and onChange instead.
  const latestRiskFreeRateCellRef = useRef(riskFreeRateCell);
  const latestOnChangeRef = useRef(onChange);
  const latestOnEmptyRiskFreeRateResolvedRef = useRef(onEmptyRiskFreeRateResolved);
  latestRiskFreeRateCellRef.current = riskFreeRateCell;
  latestOnChangeRef.current = onChange;
  latestOnEmptyRiskFreeRateResolvedRef.current = onEmptyRiskFreeRateResolved;

  // Fetched rates by maturity|date (null when the fetch returned nothing), so a rate lost to
  // a re-evaluation is re-applied on the next render instead of refetched and a failed fetch
  // is not retried on every render.
  const fetchedRiskFreeRatesRef = useRef<Record<string, number | null>>({});
  // Keys with a request in flight, so the effect and a cell edit cannot fetch the same rate twice.
  const inFlightRiskFreeRateKeysRef = useRef(new Set<string>());
  // Most recently requested key. An earlier request that resolves after it is for a maturity or
  // date the sheet has already moved off of, so its rate must not be written to the cell.
  const lastRequestedRiskFreeRateKeyRef = useRef<string | null>(null);

  const changeRiskFreeRate = useCallback(
    async (maturity, queryDate) => {
      const riskFreeRateKey = getRiskFreeRateKey(maturity, queryDate);
      // Set before the in-flight check so re-requesting a key that is still pending makes it
      // authoritative again, e.g. maturity edited from A to B and back to A.
      lastRequestedRiskFreeRateKeyRef.current = riskFreeRateKey;
      if (inFlightRiskFreeRateKeysRef.current.has(riskFreeRateKey)) {
        return;
      }
      inFlightRiskFreeRateKeysRef.current.add(riskFreeRateKey);
      try {
        const rfr = (await getRiskFreeRateByDate(
          maturity,
          moment(queryDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
        )) as number | undefined;
        // A superseded response is still valid data for its own key, so it is always cached;
        // only writing it to the cell is gated.
        fetchedRiskFreeRatesRef.current[riskFreeRateKey] = rfr ?? null;
        // Nothing will be written for an empty or 0 rate, so the cell's 0 is already final. A
        // real rate needs no report: the owner sees it land in the cell.
        if (!rfr) {
          latestOnEmptyRiskFreeRateResolvedRef.current?.(riskFreeRateKey);
        }
        if (
          rfr !== undefined
          && lastRequestedRiskFreeRateKeyRef.current === riskFreeRateKey
          && latestRiskFreeRateCellRef.current
        ) {
          latestOnChangeRef.current(latestRiskFreeRateCellRef.current, rfr);
        }
      } finally {
        inFlightRiskFreeRateKeysRef.current.delete(riskFreeRateKey);
      }
    },
    [getRiskFreeRateByDate]
  );

  const onWorkbookChange = useCallback(
    (cell, value) => {
      const opmData = getOpmData(spreadsheets);
      if (setPrevOpmData) {
        setPrevOpmData(opmData.map(cell => ({ ...cell })));
      }
      onChange(cell, value);
      if (cell.alias === MATURITY_ALIAS) {
        changeRiskFreeRate(value, backsolveDate);
      }
      if (cell.alias === OPM_BACKSOLVE_DATE_ALIAS) {
        changeRiskFreeRate(maturityValue, value);
      }
    },
    [spreadsheets, setPrevOpmData, onChange, changeRiskFreeRate, backsolveDate, maturityValue]
  );

  // Maturity and the backsolve date are set but the rate cell is empty: either this approach has
  // never fetched the rate, or a re-evaluation (or a reload after save) replaced the cell with an
  // empty one. Re-apply the rate already fetched for that pair, otherwise fetch it once. The
  // write runs here rather than inline so it happens after the commit, never during paint.
  useEffect(() => {
    if (!backsolveDate || !maturityValue || !riskFreeRateCell || hasRiskFreeRate) {
      return;
    }
    const riskFreeRateKey = getRiskFreeRateKey(maturityValue, backsolveDate);
    if (riskFreeRateKey in fetchedRiskFreeRatesRef.current) {
      const fetchedRiskFreeRate = fetchedRiskFreeRatesRef.current[riskFreeRateKey];
      // Nothing to re-apply for a failed fetch (null). A fetched 0 is skipped as well: the
      // evaluated cell already reads 0, so writing it back changes nothing and would only
      // re-run this effect on every commit.
      if (fetchedRiskFreeRate) {
        onChange(riskFreeRateCell, fetchedRiskFreeRate);
      }
      return;
    }
    changeRiskFreeRate(maturityValue, backsolveDate);
  }, [maturityValue, changeRiskFreeRate, hasRiskFreeRate, riskFreeRateCell, backsolveDate, onChange]);

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: ScalarSpreadsheet is not typed
    <ScalarSpreadsheet
      {...opmInputSheet}
      id={componentId ?? 'opm-input-table'}
      tableTerms={{ tableSlug: 'opm-input-table' }}
      onChange={onWorkbookChange}
      sheet={opmInputSheet}
      displayLegend={false}
      showToolbar
      alwaysDisplayLegend={false}
      isLedgerTable={isLedgerTable}
    />
  );
};

export default OPMInputTable;
